import { useLocation, useSearchParams } from 'react-router-dom';
import { ChevronDown, LogOut, User, Building } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useImpersonationNavigate } from '../hooks/useImpersonationNavigate';
import HeaderSearch from './header/HeaderSearch';
import HeaderNotifications from './header/HeaderNotifications';

const pageTitles = {
    '/': 'Dashboard',
    '/contacts': 'Contacts',
    '/voice-ai': 'AI Voice Receptionist',
    '/calendar': 'Calendar & Bookings',
    '/pipeline': 'Sales Pipeline',
    '/campaigns': 'Marketing Campaigns',
    '/billing': 'Billing',
    '/settings': 'Settings',
    '/inbox': 'Unified Inbox',
    '/usage': 'Usage',
    '/team': 'Team Management',
    '/social': 'Social Media',
    '/reviews': 'Reviews',
    '/invoices': 'Invoices',
    '/help': 'Help Centre',
    '/call-log': 'Call Log'
};

function Header() {
    const location = useLocation();
    const navigate = useImpersonationNavigate();
    const { user, token, logout, isOwner, isAgencyAdmin } = useAuthStore();
    const [searchParams] = useSearchParams();
    const businessId = searchParams.get('business_id') || user?.business_id;
    const [showDropdown, setShowDropdown] = useState(false);

    const pageTitle = pageTitles[location.pathname] || 'Dashboard';

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <header className="header">
            <div className="header-content">
                <div className="header-left">
                    <h1 className="page-title">{pageTitle}</h1>
                    {user?.business && (
                        <span className="badge badge-purple hide-mobile" style={{ marginLeft: 'var(--spacing-3)' }}>
                            <Building size={12} style={{ marginRight: '4px' }} />
                            {user.business.name}
                        </span>
                    )}
                    {isAgencyAdmin() && (
                        <span className="badge badge-success hide-mobile" style={{ marginLeft: 'var(--spacing-2)' }}>
                            👑 Platform Owner
                        </span>
                    )}
                    {!isAgencyAdmin() && isOwner() && (
                        <span className="badge badge-info hide-mobile" style={{ marginLeft: 'var(--spacing-2)' }}>
                            Business Owner
                        </span>
                    )}
                </div>

                <div className="header-right">
                    <HeaderSearch token={token} businessId={businessId} />
                    <HeaderNotifications token={token} />

                    {/* User Menu */}
                    <div style={{ position: 'relative' }}>
                        <button className="user-menu" onClick={() => setShowDropdown(!showDropdown)}>
                            <div className="avatar avatar-sm">
                                {user?.name?.split(' ').map(n => n[0]).join('') || 'U'}
                            </div>
                            <div className="user-info hide-mobile">
                                <span className="user-name">{user?.name || 'User'}</span>
                                <span className="user-role">
                                    {isAgencyAdmin() ? 'Platform Owner' : (isOwner() ? 'Business Owner' : 'Team Member')}
                                </span>
                            </div>
                            <ChevronDown size={16} />
                        </button>

                        {showDropdown && (
                            <>
                                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} onClick={() => setShowDropdown(false)} />
                                <div style={{
                                    position: 'absolute', right: 0, top: '100%',
                                    marginTop: 'var(--spacing-2)', width: '200px',
                                    background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
                                    borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-2)',
                                    zIndex: 100, boxShadow: 'var(--shadow-lg)'
                                }}>
                                    <div style={{ padding: 'var(--spacing-3)', borderBottom: '1px solid var(--border-primary)', marginBottom: 'var(--spacing-2)' }}>
                                        <div style={{ fontWeight: 500 }}>{user?.email}</div>
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                                            {user?.role === 'owner' ? 'Platform Owner' : 'Business Admin'}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => { setShowDropdown(false); navigate('/settings'); }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)',
                                            width: '100%', padding: 'var(--spacing-2) var(--spacing-3)',
                                            background: 'none', border: 'none', color: 'var(--text-primary)',
                                            fontSize: 'var(--font-size-sm)', cursor: 'pointer', borderRadius: 'var(--radius-md)'
                                        }}
                                    >
                                        <User size={16} /> Profile Settings
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)',
                                            width: '100%', padding: 'var(--spacing-2) var(--spacing-3)',
                                            background: 'none', border: 'none', color: 'var(--error)',
                                            fontSize: 'var(--font-size-sm)', cursor: 'pointer', borderRadius: 'var(--radius-md)'
                                        }}
                                    >
                                        <LogOut size={16} /> Sign Out
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}

export default Header;
